import { motion } from "framer-motion";
import { BsThreeDotsVertical, BsArrowRepeat } from "react-icons/bs";
import {
  FaChartLine,
  FaHistory,
  FaCalendarDay,
  FaCalendarAlt,
} from "react-icons/fa";
import { useState, useEffect } from "react";
import axiosToken from "../../api/axiosClient"; // Votre config axios
import DashboardModal from "../../components/admin/DashboardModal"; // Modal pour les détails
import { Link } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import { canAccessPath } from "../../utils/permissions";
import { getClientName } from "../../utils/clientInfo";
import { formatSalonDate, formatSalonTime } from "../../utils/salonTime";

export default function Dashboard() {
  const { user } = useAuthStore();
  // États pour les filtres
  const [salesFilter, setSalesFilter] = useState("7 derniers jours");
  const [appointmentsFilter, setAppointmentsFilter] =
    useState("7 prochains jours");
  const [historyFilter, setHistoryFilter] = useState("30 derniers jours");
  const [todayFilter, setTodayFilter] = useState("Aujourd'hui");

  // États pour les données
  const [dashboardData, setDashboardData] = useState({
    sales: [],
    recentAppointments: [],
    upcomingAppointments: [],
    todayAppointments: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // États pour les dropdowns
  const [dropdownOpen, setDropdownOpen] = useState({
    sales: false,
    appointments: false,
    history: false,
    today: false,
  });

  // Périodes disponibles
  const periodOptions = [
    "Aujourd'hui",
    "7 derniers jours",
    "30 derniers jours",
    "3 derniers mois",
    "Cette année",
    "Personnalisé",
  ];

  // Récupérer les données du dashboard
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosToken.get("/dashboard");

      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        setError(
          response.data.error || "Erreur lors de la récupération des données",
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erreur de connexion au serveur");
      console.error("Erreur dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Gestion des dropdowns
  const toggleDropdown = (dropdown) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [dropdown]: !prev[dropdown],
    }));
  };

  const handleFilterSelect = (filter, setFilter, dropdown) => {
    setFilter(filter);
    setDropdownOpen((prev) => ({
      ...prev,
      [dropdown]: false,
    }));
    // TODO: Implémenter le filtrage par période
  };

  // Ouvrir le modal avec les détails
  const openDetailsModal = (item, type) => {
    setSelectedItem({ ...item, type });
    setModalOpen(true);
  };

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Affichage du loading
  if (loading) {
    return (
      <div className="flex flex-col h-full p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white shadow-md p-4 h-[40vh]">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
                <div className="h-32 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6">
      {/* Bouton de rafraîchissement */}
      <div className="flex justify-end mb-4">
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white hover:bg-secondary-700 transition-colors"
        >
          <BsArrowRepeat className={`${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </button>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200">
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-2 px-4 py-2 bg-primary text-white hover:bg-secondary-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Grille principale */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {/* SECTION 1: Ventes récentes */}
        <div className="bg-white shadow-md flex flex-col h-[40vh]">
          {/* En-tête */}
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Ventes récentes
              </h2>
              <p className="font-poppins font-medium text-[15px] text-subtitle">
                {salesFilter}
              </p>
            </div>
            {canAccessPath(user, "/admin/sales") && (
            <div className="relative">
              <button
                onClick={() => toggleDropdown("sales")}
                className="p-1 hover:bg-gray-100"
              >
                <BsThreeDotsVertical className="text-gray-600" />
              </button>

              {dropdownOpen.sales && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 shadow-lg z-10 w-48">
                  {/* <button
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b"
                    onClick={() => {
                      // TODO: Ouvrir sélecteur de dates personnalisé
                      toggleDropdown("sales");
                    }}
                  >
                    Sélectionner un intervalle
                  </button> */}
                  <Link
                    to="/admin/sales"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Voir toutes les ventes
                  </Link>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Contenu avec scroll */}
          <div className="flex-1 overflow-y-auto p-2">
            {dashboardData.sales.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <FaChartLine size={70} className="text-gray-300 mb-3" />
                <p className="font-poppins font-bold text-[16px] text-gray-500 text-center">
                  Aucune vente récente
                </p>
                <p className="font-nunito text-[14px] text-gray-400 text-center mt-2">
                  Les ventes effectuées apparaîtront ici
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Client
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Montant
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.sales.map((sale) => (
                    <tr
                      key={sale.id}
                      onClick={() => openDetailsModal(sale, "sale")}
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="p-2 font-nunito text-sm">
                        {getClientName(sale, "Non répertorié")}
                      </td>
                      <td className="p-2 font-nunito text-sm font-medium">
                        {parseFloat(sale.amount).toFixed(2)} XOF
                      </td>
                      <td className="p-2 font-nunito text-sm text-gray-600">
                        {formatDate(sale.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* SECTION 2: Rendez-vous à venir */}
        <div className="bg-white shadow-md flex flex-col h-[40vh]">
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Rendez-vous à venir
              </h2>
              <p className="font-poppins font-medium text-[15px] text-subtitle">
                {appointmentsFilter}
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => toggleDropdown("appointments")}
                className="p-1 hover:bg-gray-100"
              >
                <BsThreeDotsVertical className="text-gray-600" />
              </button>

              {dropdownOpen.appointments && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 shadow-lg z-10 w-48">
                  {/* <button
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b"
                    onClick={() => toggleDropdown("appointments")}
                  >
                    Sélectionner un intervalle
                  </button> */}
                  <Link
                    to="/admin/workers"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Voir tous les rendez-vous
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {dashboardData.upcomingAppointments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <FaCalendarAlt size={70} className="text-gray-300 mb-3" />
                <p className="font-poppins font-bold text-[16px] text-gray-500 text-center">
                  Aucun rendez-vous à venir
                </p>
                <p className="font-nunito text-[14px] text-gray-400 text-center mt-2">
                  Les rendez-vous planifiés apparaîtront ici
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Client
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Date/Heure
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Service
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.upcomingAppointments.map((appointment) => (
                    <tr
                      key={appointment.id}
                      onClick={() =>
                        openDetailsModal(appointment, "appointment")
                      }
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="p-2 font-nunito text-sm">
                        {`${appointment.client ? appointment.client.prenom + " " + appointment.client.nom : appointment.anonymous_data ? appointment.anonymous_data.name : "Non répertorié"}`}
                      </td>
                      <td className="p-2 font-nunito text-sm">
                        <div>{formatSalonDate(appointment.date)}</div>
                        <div className="text-gray-600 text-xs">
                          {formatSalonTime(appointment.date)}
                        </div>
                      </td>
                      <td className="p-2 font-nunito text-sm">
                        {appointment.appointmentServices?.[0]?.service?.nom ||
                          "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* SECTION 3: Historique des rendez-vous */}
        <div className="bg-white shadow-md flex flex-col h-[40vh]">
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Historique des rendez-vous
              </h2>
              <p className="font-poppins font-medium text-[15px] text-subtitle">
                {historyFilter}
              </p>
            </div>
            {canAccessPath(user, "/admin/rendez-vous") && (
            <div className="relative">
              <button
                onClick={() => toggleDropdown("history")}
                className="p-1 hover:bg-gray-100"
              >
                <BsThreeDotsVertical className="text-gray-600" />
              </button>

              {dropdownOpen.history && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 shadow-lg z-10 w-48">
                  {/* <button
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b"
                    onClick={() => toggleDropdown("history")}
                  >
                    Sélectionner un intervalle
                  </button> */}
                  <Link
                    to="/admin/rendez-vous"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Voir l'historique complet
                  </Link>
                </div>
              )}
            </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {dashboardData.recentAppointments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <FaHistory size={70} className="text-gray-300 mb-3" />
                <p className="font-poppins font-bold text-[16px] text-gray-500 text-center">
                  Aucun historique
                </p>
                <p className="font-nunito text-[14px] text-gray-400 text-center mt-2">
                  L'historique des rendez-vous apparaîtra ici
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Client
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Date
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.recentAppointments.map((appointment) => (
                    <tr
                      key={appointment.id}
                      onClick={() =>
                        openDetailsModal(appointment, "appointment")
                      }
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="p-2 font-nunito text-sm">
                        {`${appointment.client ? appointment.client.prenom + " " + appointment.client.nom : appointment.anonymous_data ? appointment.anonymous_data.name : "Non répertorié"}`}
                      </td>
                      <td className="p-2 font-nunito text-sm">
                        {formatSalonDate(appointment.date)}
                      </td>
                      <td className="p-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium ${
                            appointment.status === "COMPLETED"
                              ? "bg-green-100 text-green-800"
                              : appointment.status === "CANCELLED"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {appointment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* SECTION 4: Rendez-vous du jour */}
        <div className="bg-white shadow-md flex flex-col h-[40vh]">
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h2 className="font-poppins font-semibold text-[18px] text-title">
                Rendez-vous du jour
              </h2>
              <p className="font-poppins font-medium text-[15px] text-subtitle">
                {todayFilter}
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => toggleDropdown("today")}
                className="p-1 hover:bg-gray-100"
              >
                <BsThreeDotsVertical className="text-gray-600" />
              </button>

              {dropdownOpen.today && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 shadow-lg z-10 w-48">
                  {/* <button
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b"
                    onClick={() => toggleDropdown("today")}
                  >
                    Sélectionner un intervalle
                  </button> */}
                  <Link
                    to="/admin/workers"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Voir le calendrier
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {dashboardData.todayAppointments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <FaCalendarDay size={70} className="text-gray-300 mb-3" />
                <p className="font-poppins font-bold text-[16px] text-gray-500 text-center">
                  Aucun rendez-vous aujourd'hui
                </p>
                <p className="font-nunito text-[14px] text-gray-400 text-center mt-2">
                  Les rendez-vous du jour apparaîtront ici
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Client
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Heure
                    </th>
                    <th className="text-left p-2 font-poppins text-sm text-gray-600">
                      Service
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.todayAppointments.map((appointment) => (
                    <tr
                      key={appointment.id}
                      onClick={() =>
                        openDetailsModal(appointment, "appointment")
                      }
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="p-2 font-nunito text-sm">
                        {`${appointment.client ? appointment.client.prenom + " " + appointment.client.nom : appointment.anonymous_data ? appointment.anonymous_data.name : "Non répertorié"}`}
                      </td>
                      <td className="p-2 font-nunito text-sm">
                        {formatSalonTime(appointment.date)}
                      </td>
                      <td className="p-2 font-nunito text-sm">
                        {appointment.appointmentServices?.[0]?.service?.nom ||
                          "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {/* Modal pour les détails */}
      <DashboardModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        item={selectedItem}
      />
    </div>
  );
}
// import { motion } from "framer-motion";
// import {
//   BsThreeDotsVertical,
//   BsChevronDown,
//   BsBarChartLine,
// } from "react-icons/bs";
// import { useState } from "react";
// import { FaChartLine, FaHistory } from "react-icons/fa";
// import { TbCalendarClock } from "react-icons/tb";

// export default function Dashboard() {
//   const [salesFilter, setSalesFilter] = useState("7 derniers jours");
//   const [appointmentsFilter, setAppointmentsFilter] =
//     useState("7 prochains jours");
//   const [historyFilter, setHistoryFilter] = useState("30 derniers jours");
//   const [todayFilter, setTodayFilter] = useState("Aujourd'hui");

//   const periodOptions = [
//     "Aujourd'hui",
//     "7 derniers jours",
//     "30 derniers jours",
//     "3 derniers mois",
//     "Cette année",
//     "Personnalisé",
//   ];

//   const [dropdownOpen, setDropdownOpen] = useState({
//     sales: false,
//     appointments: false,
//     history: false,
//     today: false,
//   });

//   const toggleDropdown = (dropdown) => {
//     setDropdownOpen((prev) => ({
//       ...prev,
//       [dropdown]: !prev[dropdown],
//     }));
//   };

//   const handleFilterSelect = (filter, setFilter, dropdown) => {
//     setFilter(filter);
//     setDropdownOpen((prev) => ({
//       ...prev,
//       [dropdown]: false,
//     }));
//   };

//   return (
//     <div className="flex flex-col h-full">
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.4 }}
//         className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6"
//       >
//         {/* Ventes récentes */}
//         <div className="bg-white shadow-md p-4 h-[40vh]">
//           <div className="flex flex-row justify-between items-start mb-2 p-2">
//             <div>
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Ventes récentes
//               </h2>
//               <button className="flex items-center font-poppins font-medium text-[15px] text-subtitle hover:text-gray-700">
//                 {salesFilter}
//               </button>
//             </div>
//             <div className="relative">
//               <button
//                 className="text-gray-400 hover:text-gray-600"
//                 onClick={() => toggleDropdown("sales")}
//               >
//                 <BsThreeDotsVertical />
//               </button>
//               {dropdownOpen.sales && (
//                 <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-10 min-w-[160px]">
//                   {periodOptions.map((option) => (
//                     <button
//                       key={option}
//                       className={`block w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-50 ${
//                         salesFilter === option
//                           ? "bg-secondary-50 text-secondary-600"
//                           : "text-gray-700"
//                       }`}
//                       onClick={() =>
//                         handleFilterSelect(option, setSalesFilter, "sales")
//                       }
//                     >
//                       {option}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className=" flex flex-col justify-center items-center p-4  border-gray-200">
//             <FaChartLine size={70} className="mb-3" />
//             <p className="font-poppins font-bold text-[16px] text-montant text-center">
//               Aucune donnée sur les ventes
//             </p>
//             <p className="font-nunito text-[14px] text-text text-center mt-2">
//               Prenez des rendez-vous. Les informations concernant vos ventes
//               s'afficheront ici.
//             </p>
//           </div>
//         </div>

//         {/* Rendez-vous à venir */}
//         <div className="bg-white shadow-md p-4 h-[40vh]">
//           <div className="flex flex-row justify-between items-start mb-2 p-2">
//             <div>
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Rendez-vous à venir
//               </h2>
//               <p className="flex items-center font-poppins font-medium text-[15px] text-subtitle hover:text-gray-700">
//                 {appointmentsFilter}
//               </p>
//             </div>
//             <div className="relative">
//               <button
//                 className="text-gray-400 hover:text-gray-600"
//                 onClick={() => toggleDropdown("appointments")}
//               >
//                 <BsThreeDotsVertical />
//               </button>

//               {dropdownOpen.appointments && (
//                 <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-10 min-w-[160px]">
//                   {periodOptions.map((option) => (
//                     <button
//                       key={option}
//                       className={`block w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-50 ${
//                         appointmentsFilter === option
//                           ? "bg-secondary-50 text-secondary-600"
//                           : "text-gray-700"
//                       }`}
//                       onClick={() =>
//                         handleFilterSelect(
//                           option,
//                           setAppointmentsFilter,
//                           "appointments"
//                         )
//                       }
//                     >
//                       {option}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className=" flex flex-col justify-center items-center p-4  border-gray-200">
//             <BsBarChartLine size={70} className="mb-3" />
//             <p className="font-poppins font-bold text-[16px] text-montant text-center">
//               Votre planning est vide
//             </p>
//             <p className="font-nunito text-[14px] text-text text-center mt-2">
//               Prenez des rendez-vous. Les informations concernant votre planning
//               s'afficheront ici.
//             </p>
//           </div>
//         </div>

//         {/* Historique des rendez-vous */}
//         <div className="bg-white shadow-md p-4 h-[40vh]">
//           <div className="flex flex-row justify-between items-start mb-2 p-2">
//             <div>
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Historique des rendez-vous
//               </h2>
//               <p className="flex items-center font-poppins font-medium text-[15px] text-subtitle hover:text-gray-700">
//                 {historyFilter}
//               </p>
//             </div>
//             <div className="relative">
//               <button
//                 className="text-gray-400 hover:text-gray-600"
//                 onClick={() => toggleDropdown("history")}
//               >
//                 <BsThreeDotsVertical />
//               </button>

//               {dropdownOpen.history && (
//                 <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-10 min-w-[160px]">
//                   {periodOptions.map((option) => (
//                     <button
//                       key={option}
//                       className={`block w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-50 ${
//                         historyFilter === option
//                           ? "bg-secondary-50 text-secondary-600"
//                           : "text-gray-700"
//                       }`}
//                       onClick={() =>
//                         handleFilterSelect(option, setHistoryFilter, "history")
//                       }
//                     >
//                       {option}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className=" flex flex-col justify-center items-center p-4  border-gray-200">
//             <FaHistory size={70} className="mb-3" />
//             <p className="font-poppins font-bold text-[16px] text-montant text-center">
//               Aucune activité récente
//             </p>
//             <p className="font-nunito text-[14px] text-text text-center mt-2">
//               Rendez-vous dans la rubrique calendrier pour ajouter de nouveaux
//               rendez-vous
//             </p>
//           </div>
//         </div>

//         {/* Rendez-vous du jour */}
//         <div className="bg-white shadow-md p-4 h-[40vh]">
//           <div className="flex flex-row justify-between items-start mb-2 p-2">
//             <div>
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Vos rendez-vous à venir aujourd'hui
//               </h2>
//               <p className="flex items-center font-poppins font-medium text-[15px] text-subtitle hover:text-gray-700">
//                 {todayFilter}
//               </p>
//             </div>
//             <div className="relative">
//               <button
//                 className="text-gray-400 hover:text-gray-600"
//                 onClick={() => toggleDropdown("today")}
//               >
//                 <BsThreeDotsVertical />
//               </button>
//               {dropdownOpen.today && (
//                 <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-10 min-w-[160px]">
//                   {periodOptions.map((option) => (
//                     <button
//                       key={option}
//                       className={`block w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-50 ${
//                         todayFilter === option
//                           ? "bg-secondary-50 text-secondary-600"
//                           : "text-gray-700"
//                       }`}
//                       onClick={() =>
//                         handleFilterSelect(option, setTodayFilter, "today")
//                       }
//                     >
//                       {option}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className=" flex flex-col justify-center items-center p-4  border-gray-200">
//             <TbCalendarClock size={70} className="mb-3" />
//             <p className="font-poppins font-bold text-[16px] text-montant text-center">
//               Aucun rendez-vous aujourd'hui
//             </p>
//             <p className="font-nunito text-[14px] text-text text-center mt-2">
//               Rendez-vous dans la rubrique calendrier pour ajouter de nouveaux
//               rendez-vous
//             </p>
//           </div>
//         </div>
//       </motion.div>
//     </div>
//   );
// }
